import React, { useState } from 'react';
import { Terminal, Lock, User, Eye, EyeOff, ShieldAlert, ArrowRight, Loader2, KeyRound } from 'lucide-react';
import { useAuth } from '@/services/auth/authContext';

export const LoginView: React.FC = () => {
  const { login, isLoggingIn, authError, rateLimitCountdown, clearAuthError } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const trimmedUser = username.trim();
    if (!trimmedUser) {
      setFormError('Username is required.');
      return;
    }
    if (!password) {
      setFormError('Password is required.');
      return;
    }
    if (rateLimitCountdown && rateLimitCountdown > 0) {
      return;
    }

    await login({ username: trimmedUser, password });
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (formError) setFormError(null);
    if (authError) clearAuthError();
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (formError) setFormError(null);
    if (authError) clearAuthError();
  };

  const isLockedOut = Boolean(rateLimitCountdown && rateLimitCountdown > 0);
  const displayedError = formError || authError;

  return (
    <div className="min-h-screen w-full bg-[var(--bg-app)] flex flex-col items-center justify-center p-4 selection:bg-emerald-500/20 selection:text-emerald-300">
      {/* Background glow styling */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none flex items-center justify-center">
        <div className="w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-6 sm:p-8 shadow-2xl space-y-6 relative z-10">
        {/* Brand Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex p-3 rounded-xl bg-emerald-950/80 border border-emerald-800/80 text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.2)] mb-1">
            <Terminal className="w-8 h-8" />
          </div>
          <h1 className="font-mono font-bold text-xl text-[var(--text-primary)] tracking-tight">
            TERMINAL // OPS
          </h1>
          <p className="text-xs font-mono text-[var(--text-muted)]">
            Swing & Delivery Stock Trading Bot
          </p>
        </div>

        {/* Security Notice */}
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-[var(--bg-surface-elevated)]/80 border border-[var(--border-default)] text-[11px] font-mono text-[var(--text-secondary)]">
          <KeyRound className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>Operator authentication required to access dashboard telemetry & runtime controls.</span>
        </div>

        {/* Error Alert */}
        {displayedError && (
          <div
            className="flex items-start gap-2.5 p-3 rounded-lg bg-rose-950/40 border border-rose-800/60 text-rose-300 text-xs font-mono animate-fade-in"
            role="alert"
          >
            <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <span className="font-semibold">{isLockedOut ? 'Rate Limit Exceeded' : 'Authentication Error'}</span>
              <p className="text-[11px] text-rose-300/90">{displayedError}</p>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Username */}
          <div className="space-y-1.5">
            <label
              htmlFor="username"
              className="block text-xs font-mono font-medium text-[var(--text-secondary)]"
            >
              Operator ID / Username
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-[var(--text-dim)]">
                <User className="w-4 h-4" />
              </div>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                autoFocus
                disabled={isLoggingIn || isLockedOut}
                value={username}
                onChange={handleUsernameChange}
                placeholder="Enter username"
                className="w-full pl-9 pr-3 py-2 bg-[var(--bg-input)] border border-[var(--border-default)] rounded-md text-[var(--text-primary)] font-mono text-xs placeholder-[var(--text-dim)] focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all disabled:opacity-50"
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label
              htmlFor="password"
              className="block text-xs font-mono font-medium text-[var(--text-secondary)]"
            >
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-[var(--text-dim)]">
                <Lock className="w-4 h-4" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                disabled={isLoggingIn || isLockedOut}
                value={password}
                onChange={handlePasswordChange}
                placeholder="Enter password"
                className="w-full pl-9 pr-10 py-2 bg-[var(--bg-input)] border border-[var(--border-default)] rounded-md text-[var(--text-primary)] font-mono text-xs placeholder-[var(--text-dim)] focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all disabled:opacity-50"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-[var(--text-dim)] hover:text-[var(--text-secondary)] transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoggingIn || isLockedOut || !username.trim() || !password}
            className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 disabled:bg-[var(--bg-surface-elevated)] disabled:text-[var(--text-dim)] disabled:cursor-not-allowed text-white font-mono text-xs font-semibold transition-all duration-150 active:scale-[0.99] shadow-lg shadow-emerald-950/40 cursor-pointer"
          >
            {isLoggingIn ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span>Authenticating...</span>
              </>
            ) : isLockedOut ? (
              <span>Locked ({rateLimitCountdown}s)</span>
            ) : (
              <>
                <span>Enter Terminal</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        {/* Footer info & security badge */}
        <div className="pt-4 border-t border-[var(--border-default)] flex flex-col gap-1.5 text-center text-[10px] font-mono text-[var(--text-muted)]">
          <div className="flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span>Encrypted Session (7-Day HttpOnly)</span>
          </div>
          <p className="text-[var(--text-dim)]">
            Autonomous Engine Hosted on Oracle Cloud VM
          </p>
        </div>
      </div>
    </div>
  );
};
